import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './client';

/**
 * Curated vendor lists (T-0265, T-0267; E14-design §3): Firm settings →
 * Vendor knowledge → Vendor lists. Routes in
 * `apps/api/src/routes/vendor-knowledge-lists.ts`. Every firm user reads
 * them (an engagement's vendor picker offers them); firm admins and
 * curators write.
 */

const BASE = '/api/v1/vendor-knowledge/lists';

export const VendorListSummarySchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  product_count: z.number(),
  archived_at: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type VendorListSummary = z.infer<typeof VendorListSummarySchema>;

export const VendorListProductSchema = z.object({
  product_id: z.string(),
  name: z.string(),
  platform_type: z.string(),
  status: z.string(),
  vendor: z.object({ id: z.string(), name: z.string() }),
  position: z.number(),
});
export type VendorListProduct = z.infer<typeof VendorListProductSchema>;

export const VendorListSchema = VendorListSummarySchema.extend({
  created_by: z.object({ id: z.string(), name: z.string() }),
  products: z.array(VendorListProductSchema),
});
export type VendorList = z.infer<typeof VendorListSchema>;

const ListsSchema = z.object({ lists: z.array(VendorListSummarySchema) });

export function listVendorLists(includeArchived = false) {
  return apiClient(BASE, ListsSchema, {
    query: includeArchived ? { include_archived: 'true' } : undefined,
  });
}

export function getVendorList(id: string): Promise<VendorList> {
  return apiClient(`${BASE}/${encodeURIComponent(id)}`, VendorListSchema);
}

export function createVendorList(input: {
  name: string;
  description?: string | null;
}): Promise<VendorList> {
  return apiClient(BASE, VendorListSchema, { method: 'POST', body: input });
}

export function patchVendorList(
  id: string,
  input: { name?: string; description?: string | null },
): Promise<VendorList> {
  return apiClient(`${BASE}/${encodeURIComponent(id)}`, VendorListSchema, {
    method: 'PATCH',
    body: input,
  });
}

export function archiveVendorList(id: string): Promise<VendorList> {
  return apiClient(`${BASE}/${encodeURIComponent(id)}:archive`, VendorListSchema, {
    method: 'POST',
  });
}

export function setVendorListProducts(id: string, productIds: readonly string[]) {
  return apiClient(`${BASE}/${encodeURIComponent(id)}/products`, VendorListSchema, {
    method: 'PUT',
    body: { product_ids: productIds },
  });
}

export const vendorListKeys = {
  all: () => ['vendor-lists'] as const,
  list: (includeArchived: boolean) =>
    [...vendorListKeys.all(), 'list', includeArchived ? 'all' : 'open'] as const,
  one: (id: string) => [...vendorListKeys.all(), 'one', id] as const,
};

export function useVendorLists(includeArchived = false, enabled = true) {
  return useQuery({
    queryKey: vendorListKeys.list(includeArchived),
    queryFn: () => listVendorLists(includeArchived),
    enabled,
  });
}

export function useVendorList(id: string | null) {
  return useQuery({
    queryKey: vendorListKeys.one(id ?? ''),
    queryFn: () => getVendorList(id!),
    enabled: id !== null,
  });
}

/** Any list write: the list as the server returns it, and the lists read again. */
function useListWrite<TArgs>(fn: (args: TArgs) => Promise<VendorList>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: (list) => {
      qc.setQueryData(vendorListKeys.one(list.id), list);
      return qc.invalidateQueries({ queryKey: vendorListKeys.all() });
    },
  });
}

export function useCreateVendorList() {
  return useListWrite((input: { name: string; description?: string | null }) =>
    createVendorList(input),
  );
}

export function usePatchVendorList(id: string) {
  return useListWrite((input: { name?: string; description?: string | null }) =>
    patchVendorList(id, input),
  );
}

export function useArchiveVendorList(id: string) {
  return useListWrite(() => archiveVendorList(id));
}

export function useSetVendorListProducts(id: string) {
  return useListWrite((productIds: readonly string[]) => setVendorListProducts(id, productIds));
}
